/**
 * Pure title-prefix helpers for the `title-approval-tracking` feature.
 *
 * The pageant title flow stores the full **Composed_Title** string (e.g.
 * `"Miss Boise"`) in `Participation.approvedTitle` and
 * `ApplicationResponse.titlePreferences` — the storage contract is unchanged
 * (design §"Storage model"). The **Base_Title** (`"Boise"`) is a UI-only
 * decomposition that is never persisted separately: the contestant types only
 * the base while the resolved Division_Title_Prefix is shown greyed to the
 * left, and the system composes/strips the prefix around the stored value.
 *
 * All three functions are pure and total (no throws, no I/O) so they can be
 * shared unchanged across the frontend contestant/director surfaces.
 *
 * Spec: `title-approval-tracking`
 * Validates Requirements:
 *  - 2.3 / 2.4 — prefix falls back to the division label when unset/blank
 *  - 3.1        — compose/strip round-trip around the stored Composed_Title
 *  - 3.4        — composition never double-prefixes an already-prefixed value
 */

/**
 * Compose a Composed_Title from a division prefix and a contestant-entered
 * base title.
 *
 * Rules (design §"Storage model", Requirements 3.1, 3.4):
 *  - both inputs are trimmed;
 *  - an empty (trimmed) prefix returns the trimmed base unchanged;
 *  - an empty (trimmed) base returns `''` (nothing to compose);
 *  - if the trimmed base already begins with `"<prefix> "` (case-insensitive),
 *    it is returned unchanged so the prefix is never applied twice (R3.4);
 *  - otherwise the prefix and base are joined with a single space.
 *
 * @param prefix The resolved Division_Title_Prefix (e.g. `"Miss"`).
 * @param base   The contestant-entered Base_Title (e.g. `"Boise"`).
 * @returns The Composed_Title (e.g. `"Miss Boise"`).
 */
export function compose(prefix: string, base: string): string {
  const trimmedPrefix = prefix.trim();
  const trimmedBase = base.trim();

  if (trimmedPrefix === '') {
    return trimmedBase;
  }
  if (trimmedBase === '') {
    return '';
  }

  const prefixWithSpace = `${trimmedPrefix} `;
  if (trimmedBase.toLowerCase().startsWith(prefixWithSpace.toLowerCase())) {
    return trimmedBase;
  }

  return `${trimmedPrefix} ${trimmedBase}`;
}

/**
 * Strip a division prefix from a stored Composed_Title to recover the
 * editable Base_Title for display.
 *
 * Rules (design §"Storage model", Requirements 3.1, 4.2):
 *  - both inputs are trimmed;
 *  - an empty (trimmed) prefix returns the trimmed composed value unchanged;
 *  - if the trimmed composed value begins with `"<prefix> "` (case-insensitive),
 *    the remainder after that prefix + space is returned (original casing,
 *    trimmed);
 *  - otherwise (a legacy value that does not begin with the prefix) the whole
 *    trimmed composed value is returned unchanged, so no data is lost.
 *
 * @param prefix   The resolved Division_Title_Prefix (e.g. `"Miss"`).
 * @param composed The stored Composed_Title (e.g. `"Miss Boise"`).
 * @returns The Base_Title (e.g. `"Boise"`).
 */
export function stripPrefix(prefix: string, composed: string): string {
  const trimmedPrefix = prefix.trim();
  const trimmedComposed = composed.trim();

  if (trimmedPrefix === '') {
    return trimmedComposed;
  }

  const prefixWithSpace = `${trimmedPrefix} `;
  if (trimmedComposed.toLowerCase().startsWith(prefixWithSpace.toLowerCase())) {
    return trimmedComposed.slice(prefixWithSpace.length).trim();
  }

  return trimmedComposed;
}

/**
 * Resolve the Division_Title_Prefix for a division, falling back to the
 * division label when no explicit prefix is set (Requirements 2.3, 2.4).
 *
 * @param division The division, carrying an optional `titlePrefix` and a
 *   required `label`.
 * @returns The trimmed `titlePrefix` when it is a non-empty string, otherwise
 *   the trimmed `label`.
 */
export function resolveDivisionPrefix(division: {
  titlePrefix?: string | null;
  label: string;
}): string {
  const trimmedPrefix = division.titlePrefix?.trim();
  if (trimmedPrefix) {
    return trimmedPrefix;
  }
  return division.label.trim();
}
